import { LuFrame, LuGrip, LuHexagon, LuInfo } from 'react-icons/lu';

import type { DrilledHole } from '~api/services';
import { ErrorMessage, PropertyGroup, PropertyItem, TooltipContent, TooltipRoot, TooltipTrigger } from '~components';
import { Button } from '~components/ui/button';
import { Card, CardContent } from '~components/ui/card';

type DrilledHolesInfoProps = {
  /** Array of drilled holes data */
  drilledHoles: DrilledHole[] | undefined;
};

/**
 * Computes validation and domain statistics for drilled holes
 */
const computeHoleStats = (holes: DrilledHole[] | undefined) => {
  if (!holes) {
    return {
      total: 0,
      withDrillDomain: 0,
      withBlastDomain: 0,
      invalidPlannedHoles: [],
      invalidActualHoles: [],
      byPlannedHardness: new Map(),
      byActualHardness: new Map(),
    };
  }

  const invalidPlanned: DrilledHole[] = [];
  const invalidActual: DrilledHole[] = [];
  let withDrillDomain = 0;
  let withBlastDomain = 0;

  for (const hole of holes) {
    // Check if hole has valid coordinates (actual or planned)
    const hasValidPlannedCoords = hole.collarx_planned != null && hole.collary_planned != null;
    const hasValidActualCoords = hole.collarx_actual != null && hole.collary_actual != null;

    if (!hasValidPlannedCoords) invalidPlanned.push(hole);
    if (!hasValidActualCoords) invalidActual.push(hole);

    if (hole.drill_domain_id !== undefined && hole.drill_domain_id !== null) {
      withDrillDomain += 1;
    }

    if (hole.blast_domain_id !== undefined && hole.blast_domain_id !== null) {
      withBlastDomain += 1;
    }
  }

  const byActualHardness = Map.groupBy(holes, (h) => h.hardness);
  const byPlannedHardness = Map.groupBy(holes, (h) => h.hardness_planned);

  return {
    total: holes.length,
    withDrillDomain,
    withBlastDomain,
    invalidPlannedHoles: invalidPlanned,
    invalidActualHoles: invalidActual,
    byActualHardness,
    byPlannedHardness,
  };
};

const MissingCoordsErrorMessage = ({ numInvalid }: { numInvalid?: number }) => {
  if (!numInvalid) return null;

  return (
    <ErrorMessage
      className="rounded-md p-2"
      collapsible={true}
      intent="warning"
      title={`${numInvalid} Invalid Hole${numInvalid > 1 ? 's' : ''}`}
      message={`${numInvalid} hole${numInvalid > 1 ? 's have' : ' has'} missing or invalid coordinates (x/y) and cannot be rendered.`}
    />
  );
};

/**
 * Displays statistics and validation information about drilled holes
 */
const DrilledHolesInfo = ({ drilledHoles }: DrilledHolesInfoProps) => {
  const {
    total,
    withDrillDomain,
    withBlastDomain,
    invalidPlannedHoles,
    invalidActualHoles,
    byActualHardness,
    byPlannedHardness,
  } = computeHoleStats(drilledHoles);

  if (total === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent className="space-y-4">
        <PropertyGroup withBorder>
          <PropertyItem
            label="Total Holes"
            icon={<LuGrip color="var(--color-primary)" size={16} />}
            value={total}
            variant="featured"
          />
          <PropertyItem
            label="With Drill Domain"
            icon={<LuFrame color="var(--color-primary)" size={16} />}
            value={withDrillDomain}
            variant="featured"
          />
          <PropertyItem
            label="With Blast Domains"
            icon={<LuHexagon color="var(--color-primary)" size={16} />}
            value={
              <div className="flex items-center gap-1 leading-16">
                {withBlastDomain}
                {withBlastDomain === 0 && (
                  <TooltipRoot>
                    <TooltipTrigger asChild>
                      <Button icon variant="ghost" size="sm" className="size-5">
                        <LuInfo className="size-4" color="var(--color-destructive)" aria-label="Error details" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">No Blast Domains were found.</TooltipContent>
                  </TooltipRoot>
                )}
              </div>
            }
            variant="featured"
          />
        </PropertyGroup>
        <h3 className="font-bold text-lg">Planned Hardness Statistics</h3>
        <PropertyGroup>
          {Array.from(byPlannedHardness.entries()).map(([k, holes]) => (
            <PropertyItem key={k ?? 'unknown'} label={k ?? 'Unknown'} value={holes.length} contained />
          ))}
          <MissingCoordsErrorMessage numInvalid={invalidPlannedHoles?.length} />
        </PropertyGroup>
        <div className="flex flex-h-1 border-b border-b-border" />
        <h3 className="font-bold text-lg">Actual Hardness Statistics</h3>
        <PropertyGroup>
          {Array.from(byActualHardness.entries()).map(([k, holes]) => (
            <PropertyItem key={k ?? 'unknown'} label={k ?? 'Unknown'} value={holes.length} contained />
          ))}
        </PropertyGroup>
        <MissingCoordsErrorMessage numInvalid={invalidActualHoles?.length} />
      </CardContent>
    </Card>
  );
};

export { DrilledHolesInfo };
